/**
 * useAISearchPanel Hook
 * Context-based state management for AI Search Panel visibility
 * Provides global state for showing/hiding the AI panel across the app
 */
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material';
import { useCopilotStudio } from '@hooks/useCopilotStudio';
import { Colors } from '@styles/index';

/** Offset so panel toolbar clears the fixed site navbar when panel is top: 0 */
export const chatPanelHeaderOffsetSx: SxProps = {
  pt: { xs: '56px', sm: '60px', md: '68px', lg: '72px' },
};

/** Shared desktop chat panel shell — viewport-height right pane, above navbar */
export const getDesktopChatPanelSx = (theme: Theme): SxProps => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100dvh',
  width: { md: '520px' },
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 1350,
  backgroundColor: Colors.white,
  borderRadius: 0,
  borderLeft: '1px solid rgba(168, 85, 247, 0.12)',
  boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.08)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.action.hover,
  },
});

interface AISearchPanelContextProps {
  isVisible: boolean;
  togglePanel: () => void;
  hidePanel: () => void;
  showPanel: () => void;
  isExpanded: boolean;
  toggleExpand: () => void;
  collapsePanel: () => void;
}

const AISearchPanelContext = createContext<AISearchPanelContextProps | undefined>(undefined);

/**
 * Provider component for AI Search Panel state
 */
export const AISearchPanelProvider = ({ children }: { children: ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { ensureSession } = useCopilotStudio();

  const togglePanel = useCallback(() => {
    setIsVisible((prev) => {
      if (!prev) {
        void ensureSession();
      }
      return !prev;
    });
  }, [ensureSession]);
  const hidePanel = () => setIsVisible(false);
  const showPanel = useCallback(() => {
    void ensureSession();
    setIsVisible(true);
  }, [ensureSession]);
  const toggleExpand = () => setIsExpanded((prev) => !prev);
  const collapsePanel = () => setIsExpanded(false);

  const contextValue = useMemo(
    () => ({
      isVisible,
      togglePanel,
      hidePanel,
      showPanel,
      isExpanded,
      toggleExpand,
      collapsePanel,
    }),
    [isVisible, isExpanded, showPanel, togglePanel]
  );

  return (
    <AISearchPanelContext.Provider value={contextValue}>{children}</AISearchPanelContext.Provider>
  );
};

/**
 * Hook to access AI Search Panel state
 * Must be used within AISearchPanelProvider
 */
export const useAISearchPanel = (): AISearchPanelContextProps => {
  const context = useContext(AISearchPanelContext);
  if (!context) {
    throw new Error('useAISearchPanel must be used within AISearchPanelProvider');
  }
  return context;
};
